/**
 * concierge/intents.ts — deterministic intent classification.
 *
 * Pure keyword/phrase scoring — no network, no randomness. Each intent has
 * signal phrases (strong) and keywords (weak); the best-scoring intent wins,
 * with 'unknown' when nothing clears the threshold.
 */

import type { ConciergeIntent } from './models';

interface IntentRule {
  intent: ConciergeIntent;
  /** Strong multi-word signals (3 points each). */
  phrases: string[];
  /** Weak single-token signals (1 point each). */
  keywords: string[];
}

const RULES: IntentRule[] = [
  { intent: 'recommendNextCity', phrases: ['next city', 'next destination', 'where should i go', 'recommend a city'], keywords: ['relocate', 'move'] },
  { intent: 'compareCities', phrases: ['compare'], keywords: ['vs', 'versus', 'better'] },
  { intent: 'quieterNeighborhood', phrases: ['quieter neighborhood', 'quiet neighborhood', 'quieter area', 'less noisy'], keywords: ['quieter', 'quiet', 'noise'] },
  { intent: 'bestWorkspaceToday', phrases: ['best workspace', 'where to work', 'workspace today', 'work today', 'work from today'], keywords: ['workspace'] },
  { intent: 'reduceExpenses', phrases: ['reduce my monthly expenses', 'reduce expenses', 'spend less', 'cut costs', 'save money'], keywords: [] },
  { intent: 'explainScore', phrases: ['roamer score', 'my score', 'explain my score'], keywords: ['score'] },
  { intent: 'recommendHousing', phrases: ['recommend housing', 'find housing', 'find an apartment', 'place to live', 'place to stay'], keywords: ['housing', 'apartment', 'apartments'] },
  { intent: 'planTomorrow', phrases: ['plan tomorrow', 'tomorrow plan', "tomorrow's plan"], keywords: ['tomorrow'] },
  { intent: 'cafesWithWifi', phrases: ['cafes with', 'cafés with', 'coffee shop wifi', 'cafe wifi'], keywords: ['cafe', 'cafes', 'café', 'cafés', 'wifi', 'wi-fi'] },
  { intent: 'saferNeighborhoods', phrases: ['safer neighborhoods', 'safe neighborhoods', 'safer areas', 'safe areas'], keywords: ['safer', 'safety', 'safe'] },
  { intent: 'cheaperTransportation', phrases: ['cheaper transportation', 'cheaper transport', 'cheap transport', 'transportation costs'], keywords: ['transportation', 'transport', 'transit', 'commute'] },
  { intent: 'localCustoms', phrases: ['local customs', 'local etiquette', 'cultural norms', 'local culture'], keywords: ['customs', 'etiquette', 'culture'] },
  { intent: 'weekendActivities', phrases: ['weekend activities', 'this weekend', 'weekend plans'], keywords: ['weekend'] },
  { intent: 'weekItinerary', phrases: ['one-week itinerary', 'week itinerary', 'weekly itinerary', '7-day itinerary'], keywords: ['itinerary'] },
  { intent: 'optimizeBudget', phrases: ['optimize my monthly budget', 'optimize my budget', 'monthly budget'], keywords: ['budget'] },
  { intent: 'visaRenewal', phrases: ['visa renewal', 'renew my visa', 'visa expires', 'prepare for visa'], keywords: ['visa'] },
  { intent: 'dailySummary', phrases: ["today's important updates", 'summarize today', 'daily summary', "what's new today", 'important updates'], keywords: ['updates', 'briefing'] },
];

export function classifyIntent(query: string): ConciergeIntent {
  const q = query.toLowerCase();
  const tokens = new Set(q.split(/[^a-z0-9$-]+/).filter(Boolean));
  let best: ConciergeIntent = 'unknown';
  let bestScore = 0;
  for (const rule of RULES) {
    let score = 0;
    for (const phrase of rule.phrases) if (q.includes(phrase)) score += 3;
    for (const kw of rule.keywords) if (tokens.has(kw)) score += 1;
    if (score > bestScore) {
      bestScore = score;
      best = rule.intent;
    }
  }
  return bestScore >= 1 ? best : 'unknown';
}

/** Extract candidate city names mentioned in the query from known context cities. */
export function extractMentionedCities(query: string, knownCities: string[]): string[] {
  const q = query.toLowerCase();
  return knownCities.filter((c) => q.includes(c.toLowerCase()));
}
